import { faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useCallback, useState, useMemo } from "react"
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';
import { formatDate } from "../CustomDateTools";

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));




export function ReportedSoilTable({ reportedSoils, refreshData }) {
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    sample_id: '',
    anumber: '',
    reporting_user: '',
    notes: '',
    resolved: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const handleEdit = (row) => {
    setEditingRow(row.id);
    setEditValues({
      resolved: row.resolved || false,
      resolved_notes: row.resolved_notes || ''
    });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleSave = async (id) => {
    setLoading(true);
    try {
      const response = await GoldFinderApi.put(`/admin/reported_samples/soils/${id}`, {
        resolved: editValues.resolved,
        resolved_notes: editValues.resolved_notes
      });
      
      if (response.data.done) {
        setEditingRow(null);
        setEditValues({});
        if (refreshData) {
          refreshData(); // Refresh the data in parent component
        }
      }
    } catch (error) {
      console.error('Error updating reported soil sample:', error);
      alert('Failed to update reported soil sample. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️'; // Default sort icon
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredSoils = reportedSoils.filter(row => {
    return (
      (!filters.sample_id || row.sample_id?.toString().toLowerCase().includes(filters.sample_id.toLowerCase())) &&
      (!filters.anumber || row.anumber?.toString().toLowerCase().includes(filters.anumber.toLowerCase())) &&
      (!filters.reporting_user || row.reporting_user?.toLowerCase().includes(filters.reporting_user.toLowerCase())) &&
      (!filters.notes || row.notes?.toLowerCase().includes(filters.notes.toLowerCase())) &&
      (!filters.resolved || (filters.resolved === 'true' ? row.resolved : !row.resolved))
    );
  });

  const sortedAndFilteredSoils = [...filteredSoils].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Handle different data types
    if (sortConfig.key === 'sample_id') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    } else if (sortConfig.key === 'reported_date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return (
    <div style={{ 
      maxHeight: 'calc(100vh - 400px)', 
      overflowY: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: '0.375rem'
    }}>
      <Table striped bordered hover style={{ marginBottom: 0 }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
          <tr>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('sample_id')}
            >
              Sample ID {getSortIcon('sample_id')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('anumber')}
            >
              A Number {getSortIcon('anumber')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('reporting_user')}
            >
              Reporting User {getSortIcon('reporting_user')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('reported_date')}
            >
              Date {getSortIcon('reported_date')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('notes')}
            >
              Notes {getSortIcon('notes')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('resolved')}
            >
              Resolved {getSortIcon('resolved')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('resolved_notes')}
            >
              Resolved Notes {getSortIcon('resolved_notes')}
            </th>
            <th>Actions</th>
          </tr>
          <tr>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.sample_id}
                onChange={(e) => handleFilterChange('sample_id', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.anumber}
                onChange={(e) => handleFilterChange('anumber', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.reporting_user}
                onChange={(e) => handleFilterChange('reporting_user', e.target.value)}
              />
            </th>
            <th></th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.notes}
                onChange={(e) => handleFilterChange('notes', e.target.value)}
              />
            </th>
            <th>
              <Form.Select
                size="sm"
                value={filters.resolved}
                onChange={(e) => handleFilterChange('resolved', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Resolved</option>
                <option value="false">Unresolved</option>
              </Form.Select>
            </th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredSoils.map((row, index) => (
            <tr key={row.id}>
              <td>{row.sample_id}</td>
              <td>{row.anumber}</td>
              <td>{row.reporting_user}</td>
              <td>{row.reported_date ? formatDate(new Date(row.reported_date)) : ''}</td>
              <td>{row.notes}</td>
              <td>
                {editingRow === row.id ? (
                  <Form.Check
                    type="checkbox"
                    checked={editValues.resolved}
                    onChange={(e) => handleInputChange('resolved', e.target.checked)}
                  />
                ) : (
                  row.resolved ? "True" : "False"
                )}
              </td>
              <td>
                {editingRow === row.id ? (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editValues.resolved_notes}
                    onChange={(e) => handleInputChange('resolved_notes', e.target.value)}
                    size="sm"
                  />
                ) : (
                  row.resolved_notes
                )}
              </td>
              <td>
                {editingRow === row.id ? (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleSave(row.id)}
                      disabled={loading}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faSave} />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleEdit(row)}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}